import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Avatar,
    Badge,
    Button,
    ButtonBase,
    CircularProgress,
    Snackbar,
    SxProps,
    Theme,
    Tooltip,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { ProductImage } from "../../types/productImage";
import { productImageService } from "../../services/productImageService";
import { ImageLightbox, LightboxImage } from "../media/ImageLightbox";
import { downloadBlob, fileStem } from "../media/imageActions";

export interface ProductImageViewerTarget {
    productId: string;
    productName: string;
    images: ProductImage[];
    /** Image to open on; defaults to the product's default image. */
    startImageId?: string;
}

interface ProductImageViewerProps {
    /** The product whose images to show, or null when closed. */
    target: ProductImageViewerTarget | null;
    onClose: () => void;
}

/** A product's images in display order, the way the product form arranges them. */
export function orderedProductImages(images: ProductImage[] | null | undefined): ProductImage[] {
    return [...(images ?? [])].sort((a, b) => a.display_order - b.display_order);
}

interface ProductImageButtonProps {
    src: string;
    alt: string;
    size: number;
    /** How many images the product has — shown as a badge when more than one. */
    count?: number;
    onClick: () => void;
    sx?: SxProps<Theme>;
}

// The count sits inside the thumbnail's corner: table cells clip anything
// that pokes out past the image.
const COUNT_BADGE_SX = {
    "& .MuiBadge-badge": {
        transform: "none",
        bottom: 3,
        right: 3,
        height: 18,
        minWidth: 18,
        px: 0.5,
        fontSize: 11,
    },
};

/** A product thumbnail that opens the image viewer. */
export const ProductImageButton: React.FC<ProductImageButtonProps> = ({
    src,
    alt,
    size,
    count = 1,
    onClick,
    sx,
}) => {
    const thumbnail = (
        <Avatar src={src} alt={alt} variant="rounded" sx={{ width: size, height: size, ...sx }} />
    );
    return (
        <Tooltip title={count > 1 ? `View ${count} images` : "View image"}>
            <ButtonBase
                onClick={(e) => {
                    e.stopPropagation(); // never also select/open the row
                    onClick();
                }}
                aria-label={count > 1 ? `View ${count} images of ${alt}` : `View image of ${alt}`}
                sx={{
                    borderRadius: 1,
                    flexShrink: 0,
                    cursor: "zoom-in",
                    "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
                }}
            >
                {count > 1 ? (
                    <Badge
                        badgeContent={count}
                        color="primary"
                        overlap="rectangular"
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        sx={COUNT_BADGE_SX}
                    >
                        {thumbnail}
                    </Badge>
                ) : (
                    thumbnail
                )}
            </ButtonBase>
        </Tooltip>
    );
};

// Button styling for the viewer's dark background (same as Answer Desk images).
const DOWNLOAD_SX = {
    minHeight: 44,
    minWidth: 140,
    bgcolor: "common.white",
    color: "grey.900",
    "&:hover": { bgcolor: "grey.200" },
};

/**
 * Full-size product photos: ←/→ (and swipe) through every image, and
 * download the full-resolution original of the one on screen.
 */
export const ProductImageViewer: React.FC<ProductImageViewerProps> = ({ target, onClose }) => {
    const images = useMemo(() => orderedProductImages(target?.images), [target]);
    const [index, setIndex] = useState<number | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState("");

    // Open on the requested (or default) image when a product is opened —
    // keyed by product and start image, so a parent re-render (e.g. the
    // drawer swapping in the full product) doesn't jump back to the start.
    const openKey = target ? `${target.productId}:${target.startImageId ?? ""}` : null;
    useEffect(() => {
        if (!target || images.length === 0) {
            setIndex(null);
            return;
        }
        const wanted = target.startImageId ?? images.find((img) => img.is_default)?.image_id;
        const at = images.findIndex((img) => img.image_id === wanted);
        setIndex(at >= 0 ? at : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openKey]);

    const lightboxImages: LightboxImage[] = images.map((img, i) => ({
        id: img.image_id,
        src: img.large_url || img.original_url,
        alt:
            img.alt_text ||
            (images.length > 1 ? `${target?.productName} — image ${i + 1}` : target?.productName ?? ""),
    }));

    const download = async (i: number) => {
        if (!target) return;
        const image = images[i];
        setDownloading(true);
        try {
            const blob = await productImageService.getImageBlob(target.productId, image.image_id, "original");
            const stem = fileStem(target.productName, "product");
            downloadBlob(blob, images.length > 1 ? `${stem}-${i + 1}.jpg` : `${stem}.jpg`);
        } catch {
            setError("Couldn't download the image. Check your connection and try again.");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <>
            <ImageLightbox
                images={lightboxImages}
                index={index}
                onIndexChange={setIndex}
                onClose={() => {
                    setIndex(null);
                    onClose();
                }}
                renderActions={(_, i) => (
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />}
                        onClick={() => download(i)}
                        disabled={downloading}
                        sx={DOWNLOAD_SX}
                    >
                        Download
                    </Button>
                )}
            />
            {/* Top, so it never covers the viewer's buttons on phones. */}
            <Snackbar
                open={!!error}
                autoHideDuration={4000}
                onClose={() => setError("")}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert severity="error" variant="filled" onClose={() => setError("")} sx={{ width: "100%" }}>
                    {error}
                </Alert>
            </Snackbar>
        </>
    );
};
